import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary that catches unhandled React errors and
 * shows a recovery screen instead of a blank / black screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console so it remains debuggable
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
  }

  handleReload = () => {
    // Reset the boundary state and reload the page to get a clean start
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleRetry = () => {
    // Reset the boundary state without reloading — re-renders children
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6 text-center">
        <div className="flex flex-col items-center gap-4 max-w-sm">
          <div className="w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center">
            <AlertTriangle size={28} className="text-amber-400" />
          </div>

          <h1 className="text-xl font-semibold text-slate-100">
            Something went wrong
          </h1>

          <p className="text-sm text-slate-400 leading-relaxed">
            The app ran into an unexpected error. You can try again or reload
            the app.
          </p>

          {this.state.error && (
            <pre className="mt-2 w-full text-left text-xs text-slate-500 bg-slate-800/60 rounded-lg px-3 py-2 overflow-x-auto max-h-24">
              {this.state.error.message}
            </pre>
          )}

          <div className="flex gap-3 mt-4">
            <button
              onClick={this.handleRetry}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-accent hover:bg-accent active:bg-accent-dark text-white text-sm font-medium transition-colors"
            >
              <RotateCcw size={16} />
              Try again
            </button>
            <button
              onClick={this.handleReload}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 active:bg-slate-800 text-slate-200 text-sm font-medium transition-colors"
            >
              Reload app
            </button>
          </div>
        </div>
      </div>
    );
  }
}
